import * as React from "react";
import { ArchiveIcon, BellIcon } from "lucide-react";
import { Button, Card, CardContent, cn, UserAvatar } from "@ibada/ui";

import { useTranslations } from "@/i18n";
import { PersonPreview } from "@/shared/components/person-preview";
import { formatShortRelativeTime } from "@/shared/lib/dates/date-fns-locale";
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display";
import { NotificationBadge } from "@/features/notifications/components/notification-badge";
import { useNotificationLanguage } from "@/features/notifications/hooks/use-notification-language";
import {
  type NotificationDto,
  type NotificationPriority,
  NotificationStatus,
} from "@/features/notifications/model/notifications.types";

type NotificationItemProps = {
  notification: NotificationDto;
  workspaceLabel?: string | null;
  onSelect?: (notification: NotificationDto) => void | Promise<void>;
  onArchive?: (notification: NotificationDto) => void | Promise<void>;
  /** Marks the notification read without opening it. Only offered while it is unread. */
  onMarkRead?: (notification: NotificationDto) => void | Promise<void>;
  isArchiving?: boolean;
  compact?: boolean;
  showArchiveAction?: boolean;
  surface?: "card" | "plain";
  showWorkspaceContext?: boolean;
};

function getPriorityContainerClass(priority: NotificationPriority) {
  if (priority === "CRITICAL") {
    return "border-destructive/35 bg-destructive/8";
  }

  if (priority === "HIGH") {
    return "border-primary/30 bg-primary/6";
  }

  if (priority === "LOW") {
    return "border-border/40 bg-muted/20";
  }

  return "border-border/60 bg-background/70";
}

/** Unread marker colour. Every unread item gets a clearly visible marker; only CRITICAL switches to red. */
function getPriorityAccentClass(priority: NotificationPriority) {
  if (priority === "CRITICAL") return "bg-destructive";
  return "bg-primary";
}

export function NotificationItem({
  notification,
  workspaceLabel,
  onSelect,
  onArchive,
  onMarkRead,
  isArchiving = false,
  compact = false,
  showArchiveAction = false,
  surface = "card",
  showWorkspaceContext = true,
}: NotificationItemProps) {
  const t = useTranslations("notifications");
  const tCommon = useTranslations("common");
  const language = useNotificationLanguage();
  // Title + message are rendered + localized on the backend (branch → workspace language). The client renders them
  // verbatim — no re-translation (that drifted and duplicated the entity name into the line).
  const title = notification.title?.trim() || t("fallback.title");
  const message = notification.message?.trim() ?? "";
  const createdAtLabel = React.useMemo(
    () => formatShortRelativeTime(notification.createdAt, language),
    [language, notification.createdAt],
  );
  const actorName = notification.actor?.name ?? t("systemActor");
  // Number of events rolled into this notification (e.g. "12 new pledges"); shown as a count chip when > 1.
  const groupCount = notification.groupCount ?? 1;
  // Never surface the raw system-branch name — show the localized "Parish" label instead.
  const branchLabel = notification.branch
    ? getBranchDisplayLabel({
        branchType: notification.branch.branchType,
        branchName: notification.branch.name,
        parishLabel: tCommon("scope.parish"),
        fallbackLabel: "",
      }).trim()
    : "";
  const contextLabels = [
    showWorkspaceContext ? (notification.workspace?.name ?? workspaceLabel) : null,
    branchLabel || null,
  ].filter((label): label is string => typeof label === "string" && label.trim().length > 0);
  // Priority chip only for HIGH/CRITICAL (NORMAL/LOW are visual noise); the unread dot conveys "new".
  const showPriorityChip = notification.priority === "HIGH" || notification.priority === "CRITICAL";
  const isUnread = notification.status === NotificationStatus.Unread;
  const hasInlineIndicators = isUnread || showPriorityChip;
  const canMarkRead = isUnread && Boolean(onMarkRead);
  const markReadLabel = t("actions.markRead");

  // A plain button rather than <Button variant="link">: the shared Button clips its label (overflow hidden), which cuts
  // off the offset underline, so the hover underline never shows.
  const markReadLink = canMarkRead ? (
    <button
      type="button"
      className="text-primary focus-visible:ring-ring/50 rounded-sm text-xs font-medium underline-offset-2 outline-none hover:underline focus-visible:underline focus-visible:ring-2"
      onClick={() => void onMarkRead?.(notification)}
    >
      {markReadLabel}
    </button>
  ) : null;

  if (surface === "plain" && compact) {
    return (
      // Horizontal padding lives on the row itself so the hover background reaches the panel edges.
      <div className="group/item grid grid-cols-[0.5rem_minmax(0,1fr)] gap-3 px-4 py-2.5 transition-colors hover:bg-muted/45">
        <span
          className={cn(
            "mt-1.5 inline-flex size-2 rounded-full",
            isUnread ? getPriorityAccentClass(notification.priority) : "bg-transparent",
          )}
          aria-hidden
        />
        <div className="min-w-0">
          <button
            type="button"
            className="w-full min-w-0 text-left"
            onClick={() => {
              if (!onSelect) return;
              void onSelect(notification);
            }}
          >
            <div className="flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <p className="text-foreground min-w-0 truncate text-sm font-semibold leading-snug">{title}</p>
              </div>
              <span className="text-muted-foreground shrink-0 text-xs">{createdAtLabel}</span>
            </div>
            {message ? (
              <p className="text-muted-foreground mt-1 line-clamp-1 text-xs leading-snug">{message}</p>
            ) : null}
            {contextLabels.length > 0 ? (
              <p className="text-muted-foreground/80 mt-1 truncate text-xs">
                {contextLabels.join(" · ")}
              </p>
            ) : null}
          </button>
          {markReadLink ? <div className="mt-1.5 flex justify-end">{markReadLink}</div> : null}
        </div>
      </div>
    );
  }

  const actorAvatar = (
    <UserAvatar
      className={cn("mt-0.5 shrink-0 border border-border/60", compact ? "size-7" : "size-8")}
      fallbackClassName="text-[0.7rem] font-semibold"
      name={notification.actor ? actorName : undefined}
      src={notification.actor?.avatarUrl}
      fallback={notification.actor ? undefined : <BellIcon className="size-3.5" />}
    />
  );

  const content = (
    <>
      {notification.actor ? (
        <PersonPreview
          variant="hover"
          identity={{ userId: notification.actor.id }}
          fallback={{ name: actorName, avatarUrl: notification.actor.avatarUrl }}
        >
          {actorAvatar}
        </PersonPreview>
      ) : (
        actorAvatar
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
      <button
        type="button"
        className="flex w-full min-w-0 flex-col items-start gap-1.5 text-left"
        onClick={() => {
          if (!onSelect) return;
          void onSelect(notification);
        }}
      >
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            {hasInlineIndicators ? (
              <div className="flex min-w-0 items-center gap-2">
                {notification.status === "UNREAD" ? (
                  <span className="bg-primary inline-flex size-2 shrink-0 rounded-full" aria-hidden />
                ) : null}
                {showPriorityChip ? (
                  <NotificationBadge
                    priority={notification.priority}
                    label={notification.priority === "HIGH" ? t("priority.high") : t("priority.critical")}
                  />
                ) : null}
              </div>
            ) : null}
            <p className="text-foreground flex min-w-0 items-center gap-1.5 text-sm font-medium leading-snug">
              <span className="line-clamp-1">{title}</span>
              {groupCount > 1 ? (
                <span className="bg-primary/10 text-primary shrink-0 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold leading-none">
                  {groupCount}
                </span>
              ) : null}
            </p>
            {message ? (
              <p className="text-muted-foreground line-clamp-2 text-xs leading-snug">{message}</p>
            ) : null}
          </div>
          <span className="text-muted-foreground shrink-0 text-xs">{createdAtLabel}</span>
        </div>

        {contextLabels.length > 0 ? (
          <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {contextLabels.map((label) => (
              <span key={label} className={surface === "plain" ? "max-w-32 truncate" : "rounded-full border border-border/60 px-2 py-0.5"}>{label}</span>
            ))}
          </div>
        ) : null}
      </button>
      {markReadLink}
      </div>

      {showArchiveAction ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={t("actions.archive")}
          loading={isArchiving}
          onClick={() => {
            if (!onArchive) return;
            void onArchive(notification);
          }}
        >
          <ArchiveIcon className="size-4" />
        </Button>
      ) : null}
    </>
  );

  if (surface === "plain") {
    return (
      <div
        className={cn(
          "group/item relative flex items-start gap-3 rounded-md py-3 transition-colors hover:bg-muted/60",
          compact ? "px-2" : "px-3",
        )}
      >
        <span
          className={cn(
            "absolute left-0 top-3 h-7 w-0.5 rounded-full",
            notification.status === "UNREAD" ? getPriorityAccentClass(notification.priority) : "bg-transparent",
          )}
          aria-hidden
        />
        {content}
      </div>
    );
  }

  return (
    <Card
      size="sm"
      className={cn(
        "group/item transition-colors ring-0",
        getPriorityContainerClass(notification.priority),
        notification.status === "UNREAD" ? "shadow-[inset_3px_0_0_hsl(var(--primary))]" : "",
        compact ? "py-2.5" : "",
      )}
    >
      <CardContent className="flex items-start gap-3 space-y-0">
        {content}
      </CardContent>
    </Card>
  );
}
